import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Box, Typography, Select, MenuItem } from '@mui/material';
import { Link } from 'react-router-dom';
import { getBackups, deleteBackup } from '../../services/backupService';
import { getHosts } from '../../services/hostService';
import { format } from 'date-fns';

const BackupList = () => {
  const [backups, setBackups] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [selectedHost, setSelectedHost] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [backupsData, hostsData] = await Promise.all([
          getBackups(),
          getHosts()
        ]);
        
        // Filter backups by selected host if one is selected
        const filteredBackups = selectedHost
          ? backupsData.filter(backup => backup.host._id === selectedHost)
          : backupsData;
          
        setBackups(filteredBackups);
        setHosts(hostsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedHost]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this backup?')) {
      try {
        await deleteBackup(id);
        setBackups(backups.filter(backup => backup._id !== id));
      } catch (error) {
        console.error('Error deleting backup:', error);
      }
    }
  };

  const columns = [
    { 
      field: 'host', 
      headerName: 'Host', 
      width: 200,
      valueGetter: (params) => params.row.host?.name || '',
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => (
        <Box
          sx={{
            color: params.value === 'completed' ? 'success.main' : 
                  params.value === 'failed' ? 'error.main' : 'warning.main',
            fontWeight: 'bold'
          }}
        >
          {params.value}
        </Box>
      ),
    },
    { 
      field: 'scheduledAt', 
      headerName: 'Scheduled At', 
      width: 180,
      valueGetter: (params) => format(new Date(params.value), 'PPpp'),
    },
    { 
      field: 'executedAt', 
      headerName: 'Executed At', 
      width: 180,
      valueGetter: (params) => params.value ? format(new Date(params.value), 'PPpp') : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={() => handleDelete(params.row._id)}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ height: 400, width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Backups</Typography>
        <Box>
          <Select
            value={selectedHost}
            onChange={(e) => setSelectedHost(e.target.value)}
            displayEmpty
            sx={{ mr: 2, minWidth: 200 }}
          >
            <MenuItem value="">All Hosts</MenuItem>
            {hosts.map((host) => (
              <MenuItem key={host._id} value={host._id}>
                {host.name}
              </MenuItem>
            ))}
          </Select>
          <Button component={Link} to="/backups/new" variant="contained" sx={{ mr: 2 }}>
            Create Backup
          </Button>
          <Button component={Link} to="/backups/schedule" variant="contained">
            Schedule Backup
          </Button>
        </Box>
      </Box>
      <DataGrid
        rows={backups}
        columns={columns}
        pageSize={5}
        rowsPerPageOptions={[5]}
        getRowId={(row) => row._id}
        loading={loading}
      />
    </Box>
  );
};

export default BackupList;